import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./css/layout.css";
import "./css/base.css";
import "./css/skeleton.css";
import AppBar from "./components/AppBar";
import Footer from "./components/Footer";

function Passport() {
  const navigate = useNavigate();
  
  const [raceData, setRaceData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedGender, setSelectedGender] = useState("");
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth <= 1024 : false
  );

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 1024);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  const [selectedSeason, setSelectedSeason] = useState("24/25");
  const [waterType, setWaterType] = useState("salt");
  const [selectedRace, setSelectedRace] = useState("Aquaman");

  // Race CSV URLs mapping for Salt Water
  const saltWaterRaceUrls = {
    Aquaman: 'https://firebasestorage.googleapis.com/v0/b/aquaman-5b7b3.appspot.com/o/Aquaman%20Somabay%2F2025%2Frankings%2FAQUAMAN.csv?alt=media&token=dc1dea1d-fb33-4ab6-8a77-ffeb46579a38',
    race2: 'https://firebasestorage.googleapis.com/v0/b/aquaman-5b7b3.appspot.com/o/Aquaman%20Somabay%2F2025%2Frankings%2FAQUAMAN%205.0.csv?alt=media&token=48a30aa7-018c-4a86-b916-96fab9c8e2fa',
    race3: 'https://firebasestorage.googleapis.com/v0/b/aquaman-5b7b3.appspot.com/o/Aquaman%20Somabay%2F2025%2Frankings%2FAQUAMAN%202.0.csv?alt=media&token=26738962-b2da-474d-bad2-04712c79bb09',
    race4: 'https://firebasestorage.googleapis.com/v0/b/aquaman-5b7b3.appspot.com/o/Aquaman%20Somabay%2F2025%2Frankings%2FAQUAFINS.csv?alt=media&token=1ff1eaf7-8c22-4d09-9c56-3d0889ae8794',
    race5: 'https://firebasestorage.googleapis.com/v0/b/aquaman-5b7b3.appspot.com/o/Aquaman%20Somabay%2F2025%2Frankings%2FAQUAKIDS.csv?alt=media&token=01cc3a01-05c1-40ea-8fa7-e55ceec8a327',
    race6: 'https://firebasestorage.googleapis.com/v0/b/aquaman-5b7b3.appspot.com/o/Aquaman%20Somabay%2F2025%2Frankings%2FAQUATEAMS.csv?alt=media&token=fb47844a-ba84-4e0e-95e6-d00d1e97e883'
  };

  // Race CSV URLs mapping for Fresh Water
  const freshWaterRaceUrls = {
    Aquaman: '',
    race2: 'https://firebasestorage.googleapis.com/v0/b/aquaman-5b7b3.appspot.com/o/Aquaman%20Luxor%2FRankings%2FAQUAMAN%205.0%20(1).csv?alt=media&token=866205a2-03cf-4f98-8bde-78209b254f48',
    race3: 'https://firebasestorage.googleapis.com/v0/b/aquaman-5b7b3.appspot.com/o/Aquaman%20Luxor%2FRankings%2FAQUAMAN%202.0%20(1).csv?alt=media&token=6fe63328-30f8-4c71-87de-eca60437fc5a',
    race4: 'https://firebasestorage.googleapis.com/v0/b/aquaman-5b7b3.appspot.com/o/Aquaman%20Luxor%2FRankings%2FAQUAFINS%20(1).csv?alt=media&token=2102d5e6-b601-48f2-8a4c-59e055846a77',
    race5: 'https://firebasestorage.googleapis.com/v0/b/aquaman-5b7b3.appspot.com/o/Aquaman%20Luxor%2FRankings%2FAQUAKIDS%20(1).csv?alt=media&token=453aca7f-c6af-49c5-87b6-3c41ce9b5ce8',
    race6: 'https://firebasestorage.googleapis.com/v0/b/aquaman-5b7b3.appspot.com/o/Aquaman%20Luxor%2FRankings%2FAQUATEAMS%20(1).csv?alt=media&token=4964786b-2a70-4001-95e6-47a6efb35621'
  };

  // Race display names (same for both water types)
  const raceNames = {
    Aquaman: "Aquaman",
    race2: "Aquaman 5.0",
    race3: "Aquaman 2.0",
    race4: "AquaFINS",
    race5: "AquaKids",
    race6: "AquaTeams"
  };

  // Get current race URLs based on water type
  const getCurrentRaceUrls = () => {
    return waterType === "salt" ? saltWaterRaceUrls : freshWaterRaceUrls;
  };

  // Get available races (filter out empty URLs)
  const getAvailableRaces = () => {
    const currentRaceUrls = getCurrentRaceUrls();
    return Object.entries(currentRaceUrls)
      .filter(([key, url]) => url && url.trim() !== '')
      .reduce((acc, [key, url]) => {
        acc[key] = url;
        return acc;
      }, {});
  };

  // Get available race names for dropdown
  const getAvailableRaceNames = () => {
    const availableRaces = getAvailableRaces();
    return Object.keys(availableRaces).reduce((acc, key) => {
      acc[key] = raceNames[key];
      return acc;
    }, {});
  };

  // Fetch CSV data from Firebase
  useEffect(() => {
    const fetchCSVData = async () => {
      try {
        setLoading(true);
        console.log(`Attempting to fetch CSV data for race: ${selectedRace}...`);
        
        // Try different approaches to fetch the CSV
        let response;
        const availableRaces = getAvailableRaces();
        
        // Check if selected race is available, if not use the first available race
        if (!availableRaces[selectedRace]) {
          const firstAvailableRace = Object.keys(availableRaces)[0];
          if (firstAvailableRace) {
            setSelectedRace(firstAvailableRace);
            return; // This will trigger a re-render and refetch
          } else {
            throw new Error('No races available for this water type');
          }
        }
        
        const csvUrl = availableRaces[selectedRace];
        
        try {
          // First try with CORS mode
          response = await fetch(csvUrl, {
            method: 'GET',
            mode: 'cors',
            headers: {
              'Accept': 'text/csv,text/plain,*/*'
            }
          });
        } catch (corsError) {
          console.log('CORS fetch failed, trying alternative approach...');
          // Try using a proxy or different method
          try {
            response = await fetch(`https://api.allorigins.win/raw?url=${encodeURIComponent(csvUrl)}`);
          } catch (proxyError) {
            console.log('Proxy fetch failed, trying direct fetch...');
            response = await fetch(csvUrl, {
              method: 'GET',
              mode: 'no-cors'
            });
          }
        }
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const csvText = await response.text();
        console.log('CSV data received:', csvText.substring(0, 500) + '...');
        console.log('Full CSV length:', csvText.length);
        
        // Check if we're getting HTML instead of CSV
        if (csvText.includes('<html') || csvText.includes('<!DOCTYPE') || csvText.includes('initial-scale')) {
          throw new Error('Received HTML content instead of CSV data. Check the URL.');
        }
        
        // Parse CSV data - handle both \n and \r\n line endings
        const lines = csvText.split(/\r?\n/).filter(line => line.trim());
        console.log('Lines found:', lines.length);
        
        if (lines.length < 2) {
          throw new Error('Invalid CSV format - not enough data');
        }
        
        // Better CSV parsing that handles commas within quoted fields
        const parseCSVLine = (line) => {
          const result = [];
          let current = '';
          let inQuotes = false;
          
          for (let i = 0; i < line.length; i++) {
            const char = line[i];
            if (char === '"') {
              inQuotes = !inQuotes;
            } else if (char === ',' && !inQuotes) {
              result.push(current.trim());
              current = '';
            } else {
              current += char;
            }
          }
          result.push(current.trim());
          return result;
        };
        
        const headers = parseCSVLine(lines[0]);
        console.log('Headers:', headers);
        
        const data = lines.slice(1).map((line, index) => {
          const values = parseCSVLine(line);
          return {
            pos: parseInt(values[0]) || index + 1,
            name: values[1] || '',
            bib: values[2] || '',
            gender: values[3] || '',
            team: values[4] || '',
            category: values[5] || '',
            gunTime: values[6] || '',
            chipTime: values[7] || '',
            points: parseInt(values[8]) || 0
          };
        }).filter(row => row.name && row.name !== '' && !row.name.includes('initial-scale') && !row.name.includes('sans-serif')); // Filter out empty rows and HTML content
        
        console.log('Parsed data:', data.length, 'records');
        setRaceData(data);
        setFilteredData(data);
        setError(null);
      } catch (err) {
        console.error('Error fetching CSV:', err);
        setError('Failed to load race data. Please check your internet connection and try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchCSVData();
  }, [selectedRace, waterType]);

  // Handle race selection when water type changes
  useEffect(() => {
    const availableRaces = getAvailableRaces();
    if (!availableRaces[selectedRace]) {
      const firstAvailableRace = Object.keys(availableRaces)[0];
      if (firstAvailableRace) {
        setSelectedRace(firstAvailableRace);
      }
    }
  }, [waterType]);

  const handleSearch = (term) => {
    setSearchTerm(term);
    filterData(term, selectedCategory, selectedGender);
  };

  const filterData = (search, category, gender) => {
    let filtered = raceData.filter(row => {
      const matchesSearch = !search || Object.values(row).some(value => 
        value.toString().toLowerCase().includes(search.toLowerCase())
      );
      const matchesCategory = !category || row.category === category;
      const matchesGender = !gender || row.gender === gender;
      
      return matchesSearch && matchesCategory && matchesGender;
    });
    
    setFilteredData(filtered);
  };

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    filterData(searchTerm, category, selectedGender);
  };

  const handleGenderChange = (gender) => {
    setSelectedGender(gender);
    filterData(searchTerm, selectedCategory, gender);
  };

  const getUniqueValues = (column) => {
    return [...new Set(raceData.map(row => row[column]).filter(value => value))];
  };

  return (
    <div>
      <AppBar scrolling={true} showPartnerButton={false} />
      
      {/* Header Section */}
      <div style={{ 
        backgroundColor: "#112348", 
        padding: "160px 0 20px 0", 
        marginTop: "0"
      }}>
        <div className="container">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div style={{ 
                backgroundColor: "transparent", 
                color: "white", 
                fontSize: "24px",
                padding: "8px 16px", 
                borderRadius: "4px",
                fontFamily: "Poppins",
                fontWeight: "600",
              }}>
                RANKINGS
              </div>
            </div>
            <select
              value={selectedSeason}
              onChange={(e) => setSelectedSeason(e.target.value)}
              style={{
                backgroundColor: "white",
                color: "#112348",
                padding: "8px 16px",
                borderRadius: "4px",
                fontFamily: "Poppins",
                fontWeight: "600",
                fontSize: "14px",
                border: "1px solid #e9ecef",
                outline: "none",
                cursor: "pointer",
                width: "180px"
              }}
            >
              <option value="24/25">Season 24/25</option>
              {/* <option value="23/24">Season 23/24</option>
              <option value="22/23">Season 22/23</option>
              <option value="21/22">Season 21/22</option> */}
            </select>
          </div>
          
          <div style={{ marginTop: "20px" }}>
            <div style={{
              display: "flex",
              gap: "20px",
              alignItems: "center",
              width: isMobile ? "100%" : "40%"
            }}>
              <button
                onClick={() => setWaterType("salt")}
                style={{
                  backgroundColor: "transparent",
                  color: waterType === "salt" ? "white" : "#6c757d",
                  padding: "8px 0",
                  fontFamily: "Poppins",
                  fontWeight: "600",
                  fontSize: "14px",
                  border: "none",
                  borderBottom: waterType === "salt" ? "2px solid #D69B29" : "2px solid transparent",
                  cursor: "pointer",
                  transition: "all 0.3s ease"
                }}
              >
                SALT WATER
              </button>
              <button
                onClick={() => setWaterType("fresh")}
                style={{
                  backgroundColor: "transparent",
                  color: waterType === "fresh" ? "white" : "#6c757d",
                  padding: "8px 0",
                  fontFamily: "Poppins",
                  fontWeight: "600",
                  fontSize: "14px",
                  border: "none",
                  borderBottom: waterType === "fresh" ? "2px solid #D69B29" : "2px solid transparent",
                  cursor: "pointer",
                  transition: "all 0.3s ease"
                }}
              >
                FRESH WATER
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container" style={{ padding: "40px 20px" }}>
        <h2 style={{ 
          fontFamily: "Poppins", 
          fontWeight: "900", 
          fontSize: "48px", 
          color: "#112348",
          marginBottom: "30px"
        }}>
         Race Results
        </h2>

        {/* Filters and Search */}
        <div style={{ 
          backgroundColor: "#f8f9fa",
          padding: "25px",
          borderRadius: "12px",
          marginBottom: "30px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)"
        }}>
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
            gap: "20px",
            alignItems: "end"
          }}>
            <div>
              <label style={{
                display: "block",
                marginBottom: "8px",
                fontFamily: "Poppins",
                fontWeight: "600",
                color: "#112348",
                fontSize: "14px"
              }}>
                Race
              </label>
              <select
                value={selectedRace}
                onChange={(e) => setSelectedRace(e.target.value)}
                style={{
                  width: "100%",
                  height: "48px",
                  padding: "12px 15px",
                  border: "2px solid #e9ecef",
                  borderRadius: "8px",
                  fontFamily: "Poppins",
                  fontSize: "14px",
                  backgroundColor: "white",
                  transition: "border-color 0.3s ease",
                  outline: "none"
                }}
                onFocus={(e) => e.target.style.borderColor = "#D69B29"}
                onBlur={(e) => e.target.style.borderColor = "#e9ecef"}
              >
                {Object.entries(getAvailableRaceNames()).map(([key, name]) => (
                  <option key={key} value={key}>
                    {name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label style={{
                display: "block",
                marginBottom: "8px",
                fontFamily: "Poppins",
                fontWeight: "600",
                color: "#112348",
                fontSize: "14px"
              }}>
                Category
              </label>
              <select
                value={selectedCategory}
                onChange={(e) => handleCategoryChange(e.target.value)}
                style={{
                  width: "100%",
                  height: "48px",
                  padding: "12px 15px",
                  border: "2px solid #e9ecef",
                  borderRadius: "8px",
                  fontFamily: "Poppins",
                  fontSize: "14px",
                  backgroundColor: "white",
                  transition: "border-color 0.3s ease",
                  outline: "none"
                }}
                onFocus={(e) => e.target.style.borderColor = "#D69B29"}
                onBlur={(e) => e.target.style.borderColor = "#e9ecef"}
              >
              <option value="">All Categories</option>
              {getUniqueValues('category').map(category => (
                <option key={category} value={category}>{category}</option>
              ))}
              </select>
            </div>

            <div>
              <label style={{
                display: "block",
                marginBottom: "8px",
                fontFamily: "Poppins",
                fontWeight: "600",
                color: "#112348",
                fontSize: "14px"
              }}>
                Gender
              </label>
              <select
                value={selectedGender}
                onChange={(e) => handleGenderChange(e.target.value)}
                style={{
                  width: "100%",
                  height: "48px",
                  padding: "12px 15px",
                  border: "2px solid #e9ecef",
                  borderRadius: "8px",
                  fontFamily: "Poppins",
                  fontSize: "14px",
                  backgroundColor: "white",
                  transition: "border-color 0.3s ease",
                  outline: "none"
                }}
                onFocus={(e) => e.target.style.borderColor = "#D69B29"}
                onBlur={(e) => e.target.style.borderColor = "#e9ecef"}
              >
              <option value="">All Genders</option>
              <option value="M">Male</option>
              <option value="F">Female</option>
              </select>
            </div>


            <div>
              <label style={{
                display: "block",
                marginBottom: "8px",
                fontFamily: "Poppins",
                fontWeight: "600",
                color: "#112348",
                fontSize: "14px"
              }}>
                Search
              </label>
              <input
                type="text"
                placeholder="Search by name, bib number, or time..."
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                style={{
                  width: "100%",
                  height: "48px",
                  padding: "12px 15px",
                  border: "2px solid #e9ecef",
                  borderRadius: "8px",
                  fontFamily: "Poppins",
                  fontSize: "14px",
                  marginBottom: "0px",
                  transition: "border-color 0.3s ease",
                  outline: "none"
                }}
                onFocus={(e) => e.target.style.borderColor = "#D69B29"}
                onBlur={(e) => e.target.style.borderColor = "#e9ecef"}
              />
            </div>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div style={{ 
            textAlign: "center", 
            padding: "60px 20px",
            backgroundColor: "white",
            borderRadius: "8px",
            boxShadow: "0 2px 10px rgba(0,0,0,0.1)"
          }}>
            <div style={{
              fontFamily: "Poppins",
              fontSize: "18px",
              color: "#112348",
              fontWeight: "600"
            }}>
              Loading race results...
            </div>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div style={{ 
            textAlign: "center", 
            padding: "40px 20px",
            backgroundColor: "#f8d7da",
            borderRadius: "8px",
            boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
            border: "1px solid #f5c6cb",
            marginBottom: "20px"
          }}>
            <div style={{
              fontFamily: "Poppins",
              fontSize: "16px",
              color: "#721c24",
              fontWeight: "600",
              marginBottom: "15px"
            }}>
              ❌ {error}
            </div>
            <button
              onClick={() => window.location.reload()}
              style={{
                backgroundColor: "#D69B29",
                color: "#112348",
                border: "none",
                padding: "8px 16px",
                borderRadius: "4px",
                fontFamily: "Poppins",
                fontWeight: "600",
                fontSize: "14px",
                cursor: "pointer"
              }}
            >
              Retry Loading Data
            </button>
          </div>
        )}

        {/* Results Table */}
        {!loading && !error && (
          <div style={{ 
            backgroundColor: "white", 
            borderRadius: "8px", 
            overflow: "hidden",
            boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
            maxHeight: "70vh",
            overflowY: "auto"
          }}>
            <table style={{ 
              width: "100%", 
              borderCollapse: "collapse",
              fontFamily: "Poppins"
            }}>
              <thead style={{ position: "sticky", top: 0, zIndex: 10 }}>
                <tr style={{ backgroundColor: "#112348" }}>
                  <th style={{
                    padding: isMobile ? "18px 10px" : "15px 20px",
                    color: "white",
                    fontFamily: "Poppins",
                    fontWeight: "600",
                    fontSize: isMobile ? "16px" : "14px",
                    textAlign: "left",
                    borderBottom: "2px solid #D69B29",
                    width: isMobile ? "15%" : "auto"
                  }}>
                    RANK
                  </th>
                  <th style={{
                    padding: isMobile ? "18px 10px" : "15px 20px",
                    color: "white",
                    fontFamily: "Poppins",
                    fontWeight: "600",
                    fontSize: isMobile ? "16px" : "14px",
                    textAlign: "left",
                    borderBottom: "2px solid #D69B29",
                    width: isMobile ? "60%" : "auto"
                  }}>
                    NAME
                  </th>
                  {!isMobile && (
                  <th style={{
                    padding: "15px 20px",
                    color: "white",
                    fontFamily: "Poppins",
                    fontWeight: "600",
                    fontSize: "14px",
                    textAlign: "left",
                    borderBottom: "2px solid #D69B29"
                  }}>
                    BIB
                  </th>
                  )}
                  {!isMobile && (
                  <th style={{
                    padding: "15px 20px",
                    color: "white",
                    fontFamily: "Poppins",
                    fontWeight: "600",
                    fontSize: "14px",
                    textAlign: "left",
                    borderBottom: "2px solid #D69B29"
                  }}>
                    M/F
                  </th>
                  )}
                  {!isMobile && (
                  <th style={{
                    padding: "15px 20px",
                    color: "white",
                    fontFamily: "Poppins",
                    fontWeight: "600",
                    fontSize: "14px",
                    textAlign: "left",
                    borderBottom: "2px solid #D69B29"
                  }}>
                    TEAM/CLUB
                  </th>
                  )}
                  {!isMobile && (
                  <th style={{
                    padding: "15px 20px",
                    color: "white",
                    fontFamily: "Poppins",
                    fontWeight: "600",
                    fontSize: "14px",
                    textAlign: "left",
                    borderBottom: "2px solid #D69B29"
                  }}>
                    CATEGORY
                  </th>
                  )}
                  {!isMobile && (
                  <th style={{
                    padding: "15px 20px",
                    color: "white",
                    fontFamily: "Poppins",
                    fontWeight: "600",
                    fontSize: "14px",
                    textAlign: "left",
                    borderBottom: "2px solid #D69B29"
                  }}>
                    TIME
                  </th>
                  )}
                  <th style={{
                    padding: isMobile ? "18px 10px" : "15px 20px",
                    color: "white",
                    fontFamily: "Poppins",
                    fontWeight: "600",
                    fontSize: isMobile ? "16px" : "14px",
                    textAlign: "left",
                    borderBottom: "2px solid #D69B29",
                    width: isMobile ? "25%" : "auto"
                  }}>
                    POINTS
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredData.map((row, index) => (
                  <tr key={index} style={{
                    backgroundColor: index % 2 === 0 ? "white" : "#f8f9fa",
                    borderBottom: "1px solid #eee"
                  }}>
                    <td style={{
                      padding: isMobile ? "15px 10px" : "12px 20px",
                      color: "#112348",
                      fontFamily: "Poppins",
                      fontSize: isMobile ? "16px" : "14px",
                      fontWeight: "600",
                      width: isMobile ? "15%" : "auto"
                    }}>
                      {row.pos}
                    </td>
                    <td style={{
                      padding: isMobile ? "15px 10px" : "12px 20px",
                      color: "#112348",
                      fontFamily: "Poppins",
                      fontSize: isMobile ? "16px" : "14px",
                      fontWeight: "600",
                      width: isMobile ? "60%" : "auto"
                    }}>
                      {row.name}
                    </td>
                    {!isMobile && (
                    <td style={{
                      padding: "12px 20px",
                      color: "#112348",
                      fontFamily: "Poppins",
                      fontSize: "14px"
                    }}>
                      {row.bib}
                    </td>
                    )}
                    {!isMobile && (
                    <td style={{
                      padding: "12px 20px",
                      color: "#112348",
                      fontFamily: "Poppins",
                      fontSize: "14px"
                    }}>
                      {row.gender}
                    </td>
                    )}
                    {!isMobile && (
                    <td style={{
                      padding: "12px 20px",
                      color: "#112348",
                      fontFamily: "Poppins",
                      fontSize: "14px"
                    }}>
                      {row.team}
                    </td>
                    )}
                    {!isMobile && (
                    <td style={{
                      padding: "12px 20px",
                      color: "#112348",
                      fontFamily: "Poppins",
                      fontSize: "14px"
                    }}>
                      {row.category}
                    </td>
                    )}
                    {!isMobile && (
                    <td style={{
                      padding: "12px 20px",
                      color: "#112348",
                      fontFamily: "Poppins",
                      fontSize: "14px"
                    }}>
                      {row.gunTime}
                    </td>
                    )}
                    <td style={{
                      padding: isMobile ? "15px 10px" : "12px 20px",
                      color: "#112348",
                      fontFamily: "Poppins",
                      fontSize: isMobile ? "16px" : "14px",
                      fontWeight: "600",
                      width: isMobile ? "25%" : "auto"
                    }}>
                      {row.points}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Back to Home Button */}
        {/* <div style={{ textAlign: "center", marginTop: "30px" }}>
          <button
            onClick={() => navigate('/')}
            style={{
              backgroundColor: "#D69B29",
              color: "#112348",
              border: "none",
              padding: "10px 20px",
              borderRadius: "6px",
              fontFamily: "Poppins",
              fontWeight: "600",
              fontSize: "14px",
              cursor: "pointer"
            }}
          >
            Back to Home
          </button>
        </div> */}
      </div>

      <Footer />
    </div>
  );
}

export default Passport;
